import styled, { keyframes, css } from 'styled-components/macro';
import modularScale from 'styles/helpers/modular-scale';
import {
  Section,
  Heading,
} from 'components/search/ResultsSection/ResultsSection.style';
import { showMoreButton } from 'styles/base-classes';
import { Label, Fieldset } from 'components/base/RadioGroup/RadioGroup';
import { MenuRoot } from 'components/base/Menu';
import { NoRecentEntitiesWrapper } from '../RecentEntities/RecentEntities';
import {
  ModalCloseButton,
  ListViewContentWrapper,
} from 'components/customlists/shared/styles';
import Box from 'components/elements/Box';
import { Wrapper, Text } from './../../Switcher/styles';

const fadeIn = keyframes`
  0%{
    opacity: 0;
  }
  100%{
    opacity: 1;
  }
`;

export const SearchWrapper = styled('div')<{ isIE?: boolean }>`
  height: 70px;
  width: 560px;
  display: ${({ isIE }): string => (isIE ? 'block' : 'flex')};
  justify-content: center;
  margin: 0 auto 56px;
  position: relative;

  @media screen and (max-width: 40em) {
    width: 100%;
  }
`;

export const ResultDropdownSection = styled('div')`
  max-height: 670px;
  width: 100%;
  background: ${({ theme }): string => theme.White};
  position: absolute;
  margin-top: ${({ theme }): string => theme.spacing.xsmall};
  box-shadow: 0 2px 4px 0 rgba(137, 131, 131, 0.5);
  padding-top: 89px;
  animation-name: ${fadeIn};
  animation-duration: 200ms;

  ${Label} {
    font-weight: 600;
    font-size: 16px;
    min-width: 4em;
    padding: ${({ theme }): string => theme.spacing.small};

    &:not(:last-of-type) {
      border-right: initial;
    }
  }

  ${Fieldset} {
    padding: 0 20px;
  }

  ${Heading} {
    margin-bottom: ${({ theme }): string => theme.spacing.small};
  }

  @media screen and (max-width: 40em) {
    padding-top: 86px;

    ${MenuRoot} {
      margin: ${({ theme }): string => `0 ${theme.spacing.medium}`};
    }
  }
`;

export const ResultItemsWrapper = styled('div')`
  overflow-y: scroll;
  overflow-x: hidden;
  max-height: 500px;
  margin-top: ${({ theme }): string => theme.spacing.large};

  /* Customize the scroll thumb */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    width: 4px;
    border-radius: 3.5px;
    background-color: ${({ theme }): string => theme.LightGrey};
  }
  scrollbar-color: ${({ theme }): string =>
    `${theme.LightGrey} ${theme.White}`};
  scrollbar-width: thin;

  @media screen and (min-width: 40em) {
    margin-right: 7px;
    margin-bottom: 10px;
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-thumb {
      width: 8px;
    }
  }

  ${Section} {
    margin: ${({ theme }): string =>
      `${theme.spacing.medium} 9px ${theme.spacing.small} 24px`};

    &:first-of-type {
      margin-top: 0;
    }

    @media screen and (max-width: 40em) {
      margin: ${({ theme }): string =>
        `${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.large}`};
      overflow: hidden;
    }
  }
`;

export const RecentEntitiesWrapper = styled('div')`
  margin: ${({ theme }): string =>
    `${theme.spacing.medium} 9px ${theme.spacing.small} 24px`};

  &:first-of-type {
    margin-top: 0;
  }

  @media screen and (max-width: 40em) {
    margin: ${({ theme }): string =>
      `${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.small}`};
    overflow: hidden;

    ${NoRecentEntitiesWrapper} {
      margin: ${({ theme }): string =>
        `0 ${theme.spacing.medium} ${theme.spacing.medium}`};
    }
  }
`;

export const ButtonWrapper = styled('div')`
  margin: 0 -${({ theme }): string => theme.spacing.small};

  button {
    ${showMoreButton};

    @media screen and (max-width: 40em) {
      padding-left: ${({ theme }): string => theme.spacing.small};
      text-align: center;
    }
  }
`;

export const ClearRecent = styled('div')`
  margin-left: 0 !important;
  button {
    ${showMoreButton};
    padding-left: 0;

    svg {
      margin-right: ${({ theme }): string => theme.spacing.xsmall}!important;
    }
  }
`;

export const RecentButtonWrapper = styled('div')<any>`
  display: flex;
  justify-content: space-between;
  margin-left: ${({ theme }): string => theme.spacing.huge};

  @media screen and (max-width: 40em) {
    margin-left: 0;
  }

  button {
    ${showMoreButton};
    padding: ${({ theme }): string => theme.spacing.small}!important;
  }

  ${({ hiddenShowAll }): any =>
    hiddenShowAll &&
    css`
      justify-content: flex-end;
      ${ButtonWrapper} {
        display: none;
      }
    `}

  ${({ hiddenClear }): any =>
    hiddenClear &&
    css`
      ${ClearRecent} {
        display: none;
      }
    `}
`;

// Input

const iconStyles = (): any => css`
  display: flex;

  svg {
    width: ${({ theme }): string => theme.spacing.medium};
    height: ${({ theme }): string => theme.spacing.medium};
  }
`;

export const SearchIconWrapper = styled('div')`
  ${iconStyles};
  padding: ${({ theme }): string =>
    `${theme.spacing.small} ${theme.spacing.xsmall} ${theme.spacing.small} ${theme.spacing.medium}`};

  svg {
    color: white;
  }
`;

export const MicrophoneIconWrapper = styled('div')<any>`
  ${iconStyles};
  padding-right: ${({ theme }): string => theme.spacing.medium};
  cursor: pointer;

  svg {
    fill: ${({ theme, active }): string =>
      active ? theme.AccentGreen : theme.White};
  }
`;

export const Form = styled('form')<{ active: boolean }>`
  margin: ${({ theme }): string =>
    `${theme.spacing.xsmall} 0 ${theme.spacing.small}`};
  border-radius: 2px;
  background-color: rgba(5, 102, 112, 0.2);
  display: flex;
  justify-content: space-between;
  width: 560px;
  height: 60px;
  align-items: center;
  z-index: 1;
  position: absolute;

  ::after {
    content: '';
    background-color: ${({ theme }): string => theme.AccentGreen};
    display: inline-block;
    height: 1px;
    position: absolute;
    -webkit-transform: scale(0, 1);
    transform: scale(0, 1);
    -webkit-transition: all 0.3s linear;
    transition: all 0.3s linear;
    width: calc(100% - 40px);
    bottom: 0;
    margin: 0 20px;
  }

  @media screen and (max-width: 40em) {
    width: 100%;

    ::after {
      margin: ${({ theme }): string => `0 ${theme.spacing.medium}`};
      width: ${({ theme }): string =>
        `calc(100% - ${theme.spacing.medium} * 2);`};
    }
  }

  ${({ active }): any =>
    active &&
    css`
      opacity: 1;
      background-color: unset;
      margin: ${({ theme }): string => `${theme.spacing.xsmall} auto`};
      caret-color: ${({ theme }): string => theme.AccentGreen};
      ${Input} {
        /* color: ${({ theme }): string => theme.AccentGreen}; */
        color: ${({ theme }): string => theme.DeepBlue};
      }

      ${SearchIconWrapper} {
        svg {
          color: ${({ theme }): string => theme.AccentGreen};
          width: ${({ theme }): string => theme.spacing.medium};
          height: ${({ theme }): string => theme.spacing.medium};
        }
      }

      ::after {
        transform: scale(1);
      }
    `}
`;

export const Input = styled('input')`
  appearance: none;
  width: 100%;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  outline: none;
  color: white;
  font-size: ${modularScale(2)};
  font-family: inherit;
  overflow: auto;
  line-height: 43px;
  font-size: 16px;
  padding: 1px 30px 0 0;

  &::placeholder {
    color: ${({ theme }): string => theme.White};
    opacity: 1;
  }

  ::-ms-clear {
    display: none;
  }
`;

export const AddToListSection = styled('div')`
  max-height: 670px;
  margin-top: ${({ theme }): string => theme.spacing.xsmall};
  width: 100%;
  background: ${({ theme }): string => theme.White};
  position: absolute;
  top: 0;
  box-shadow: 0 2px 4px 0 rgba(137, 131, 131, 0.5);

  ${ModalCloseButton} {
    display: none;
  }

  ${ListViewContentWrapper} {
    padding: 0 20px;

    h1 {
      margin-top: 1rem;
    }

    section {
      display: flex;
      justify-content: space-between;
      align-items: baseline;

      ${Box} {
        margin-right: ${({ theme }): string => theme.spacing.xsmall};
      }
    }
  }
`;

const sound = keyframes`
	from, to { transform: scale(1); }
  10% { transform: scale(1.2); }
  50% { transform: scale(1); }
  80% { transform: scale(1.3); }
`;

export const AnimatedRing = styled(Box)`
  background-color: white;
  border-radius: 50%;
  width: 6.5em;
  height: 6.5em;
  left: calc(50% - 3.25em);
  position: absolute;
  opacity: 0.2;
  animation: ${sound} 1s infinite ease-out;
`;

export const IconWrapper = styled('div')`
  position: relative;
`;

export const CompilationSwitcher = styled(Box)`
  ${Wrapper} {
    margin: 0 0 1rem 1.2rem !important;

    ${Text} {
      font-weight: 600;
    }
  }
`;
